import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import Row from 'react-bootstrap/Row';
import cx from 'classnames';
import FormInput from '../../Form/FormInput';
import useQueryParam from "../../../utilities/useQueryParam";
import { search } from '../../../services/SearchService';
import Loader from '../../Loader';
import './SearchPage.style.scss';
import IBuildrProps from '../../../utilities/IBuildrProps';
import { AutomationPlacement, AutomationTestIdElementType, AutomationTestIdFeature, createDataTestId } from '../../../utilities/automation';

export interface SearchPageProps extends IBuildrProps {
    maxPageSize: number;
    maxPaginationLinks: number;
}

export default function SearchPage(searchConfig: SearchPageProps) {
    const [urlQuery, setUrlQuery] = useQueryParam('q', '');
    const [urlPageType, setUrlPageType] = useQueryParam('pageType', '');
    const [urlPageNumber, setUrlPageNumber] = useQueryParam('page', '1');
    const [inputValue, setInputValue] = useState(urlQuery);

    function handleInputChange(e) {
        setInputValue(e.target.value);
    }

    function handleSearch() {
        if (inputValue.trim()) {
            setUrlPageNumber('1');
            setUrlQuery(inputValue);
        }
    }

    function handlePageChange(pageNum) {
        setUrlPageNumber(pageNum);
    }

    return (
        <div className="py-6 px-xl-6 px-lg-5 px-4">
            <h2 className="text-primary text-serif">Search results</h2>

            <div className="mt-4">
                <FormInput id="search" label="Search" value={inputValue} onChange={handleInputChange} onEnter={handleSearch} darkBg data-test-id={createDataTestId({placement: AutomationPlacement.SEARCH_PAGE, feature: AutomationTestIdFeature.SEARCH, element: AutomationTestIdElementType.INPUT})}/>
                <Button className="py-2" type="button" onClick={handleSearch} data-test-id={createDataTestId({placement: AutomationPlacement.SEARCH_PAGE, feature: AutomationTestIdFeature.SEARCH, element: AutomationTestIdElementType.BUTTON})}>Search</Button>
            </div>

            <div className="mt-4">
                <SearchResults
                    searchConfig={searchConfig}
                    pageNumber={Number(urlPageNumber) || 1}
                    query={urlQuery}
                    pageType={urlPageType}
                    onPageChange={handlePageChange}/>
            </div>
        </div>
    )
}

function SearchResults({ searchConfig, pageNumber, query, pageType, onPageChange}) {
    const [results, setResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [totalResultsFound, setTotalResultsFound] = useState(0);
    const canLoad = useRef(false);

    useEffect(() => {
        if (query.trim()) {
            setIsSearching(true);
            search({ query, pageNumber, limit: searchConfig.maxPageSize, pageType })
                .then(response => {
                    setTotalResultsFound(response.numFound);
                    setResults(response.docs);
                    canLoad.current = true;
                    setIsSearching(false);
                })
                .catch(() => {
                    setIsSearching(false);
                    setResults([]);
                });
        }
    }, [query, pageNumber, pageType]);

    return (
        <div>
            { results.length > 0 ?
                <>
                    <ul className="p-0 list-unstyled">
                        {results.map(result => (
                            <SearchResult key={result.id} className="mb-4" {...result} />
                        ))}
                    </ul>
                    <Pagination
                        searchConfig={searchConfig}
                        currentPage={pageNumber}
                        query={query}
                        totalPageCount={Math.ceil(totalResultsFound/searchConfig.maxPageSize)}
                        onPageChange={onPageChange} />
                </>
                :
                <h5>No results found</h5>
            }
            {isSearching &&
                <Row className="position-relative w-100 justify-content-center mb-4">
                    <Loader spinner={isSearching} top={40} width={4} height={4} variant="secondary" />
                </Row>
            }
        </div>
    )
}
function Pagination({ searchConfig, currentPage, query, totalPageCount, onPageChange }) {

    const [prevPageNum, setPrevPageNum] = useState(0);
    const [nextPageNum, setNextPageNum] = useState(0);
    const [leftDotsPageNum, setLeftDotsPageNum] = useState(0);
    const [rightDotsPageNum, setRightDotsPageNum] = useState(0);
    const [showLeftDots, setShowLeftDots] = useState(false);
    const [showRightDots, setShowRightDots] = useState(false);
    const [pageRange, setPageRange] = useState([]);

    useEffect(() => {
        const maxPageLinks = Math.min(searchConfig.maxPaginationLinks, totalPageCount);
        setPageRange(Array.from(Array(maxPageLinks).keys()).map(num => {
            let pageLinkOffset = 1;

            if (currentPage > (totalPageCount - Math.floor(maxPageLinks/2))) {
                pageLinkOffset = totalPageCount - (maxPageLinks - 1);
            } else if (currentPage > Math.ceil(maxPageLinks/2)) {
                pageLinkOffset = currentPage - Math.floor(maxPageLinks/2);
            }

            return num + pageLinkOffset;
        }));

        setShowLeftDots(pageRange[0] !== 1);
        setShowRightDots(pageRange[pageRange.length - 1] !== totalPageCount);
        setLeftDotsPageNum(Math.max(Math.ceil(maxPageLinks/2), currentPage - maxPageLinks));
        setRightDotsPageNum(Math.min(totalPageCount - Math.ceil(maxPageLinks/2) + 1, currentPage + maxPageLinks));
        setPrevPageNum(currentPage > 1 ? currentPage - 1 : currentPage);
        setNextPageNum(currentPage < totalPageCount ? currentPage + 1 : currentPage);

    }, [currentPage, totalPageCount, prevPageNum, nextPageNum]);

    function handlePageClick(e, pageNum) {
        e.preventDefault();
        onPageChange(`${pageNum}`);
    }

    return (
        <ul className="d-flex list-inline">
            <li className="list-inline-item">
                <a
                    className={cx('pagination-link', {disabled: currentPage === 1})}
                    href={currentPage === 1 ? null : `/search?q=${query}`}
                    onClick={(e) => handlePageClick(e, 1)}>
                    {'|<'}
                </a>
            </li>
            <li className="list-inline-item">
                <a
                    className={cx('pagination-link', {disabled: currentPage === 1})}
                    href={currentPage === 1 ? null : `/search?q=${query}&page=${prevPageNum}`}
                    onClick={(e) => handlePageClick(e, prevPageNum)}>
                    {'<'}
                </a>
            </li>
            {showLeftDots &&
                <li className="list-inline-item">
                    <a
                        className={cx('pagination-link')}
                        href={`/search?q=${query}&page=${leftDotsPageNum}`}
                        onClick={(e) => handlePageClick(e, leftDotsPageNum)}>
                        ...
                    </a>
                </li>
            }
            {pageRange.map((pageNumber) => {
                return (
                    <li className="list-inline-item" key={pageNumber}>
                        <a
                            className={cx('pagination-link', {'on-current': pageNumber === currentPage})}
                            href={pageNumber === currentPage ? null : `/search?q=${query}&page=${pageNumber}`}
                            onClick={(e) => handlePageClick(e, pageNumber)}>
                            {pageNumber}
                        </a>
                    </li>
                );
            })}
            {showRightDots &&
                <li className="list-inline-item">
                    <a
                        className={cx('pagination-link')}
                        href={`/search?q=${query}&page=${rightDotsPageNum}`}
                        onClick={(e) => handlePageClick(e, rightDotsPageNum)}>
                        ...
                    </a>
                </li>
            }
            <li className="list-inline-item">
                <a
                    className={cx('pagination-link', {disabled: currentPage === totalPageCount})}
                    href={currentPage === totalPageCount ? null : `/search?q=${query}&page=${nextPageNum}`}
                    onClick={(e) => handlePageClick(e, nextPageNum)}>
                    {'>'}
                </a>
            </li>
            <li className="list-inline-item">
                <a
                    className={cx('pagination-link', {disabled: currentPage === totalPageCount})}
                    href={currentPage === totalPageCount ? null : `/search?q=${query}&page=${totalPageCount}`}
                    onClick={(e) => handlePageClick(e, totalPageCount)}>
                    {'>|'}
                </a>
            </li>
            <li className="list-inline-item">
                Page {currentPage} of {totalPageCount}
            </li>
        </ul>
    )
}
function SearchResult({ title, path, description, className }) {
    return (
        <li className={className}>
            <a href={path} className="text-dark text-decoration-none">
                <h5>{title}</h5>
                <div style={{ color: "#006621" }}>
                    <span>{path}</span>
                </div>
            </a>
            <div>
                <p>{description}</p>
            </div>
        </li>
    )
}